import type { CSSProperties, HTMLAttributes, ReactNode } from 'react';

import { Icon, type IconName } from '../core/Icon';

export interface DesktopNavItem {
  readonly key: string;
  readonly label: string;
  readonly icon?: IconName;
}

export interface DesktopNavProps extends Omit<HTMLAttributes<HTMLElement>, 'style'> {
  readonly items?: readonly DesktopNavItem[];
  readonly active?: string;
  readonly onNavigate?: (key: string) => void;
  /** Contrôles MODE / ÉTAT (`ContextStatus`), affichés après les entrées de navigation et séparés
   * d'elles par un trait vertical — jamais dupliqués ailleurs (voir revue visuelle « corrections
   * visuelles desktop »). */
  readonly trailing?: ReactNode;
  readonly style?: CSSProperties;
}

/**
 * Navigation desktop autonome — même jeu d'entrées (`key`/`label`/`icon`) que `MobileNav`, pour
 * un usage hors du chrome de `AppHeader` (ex. panneau latéral). Piste horizontale, soulignement
 * de l'entrée active, jamais de couleur seule pour porter l'information (accent + soulignement).
 */
export function DesktopNav({ items = [], active, onNavigate, trailing, style, ...rest }: DesktopNavProps) {
  return (
    <nav
      aria-label="Navigation principale"
      {...rest}
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 26,
        ...style,
      }}
    >
      {items.map((item) => {
        const on = item.key === active;
        return (
          <button
            key={item.key}
            type="button"
            onClick={() => onNavigate?.(item.key)}
            aria-current={on ? 'page' : undefined}
            className="ai-nav-item ai-focusable"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 8,
              background: 'none',
              border: 0,
              padding: '4px 0',
              cursor: 'pointer',
              fontFamily: 'var(--font-ui)',
              /* Même taille que les libellés MODE/ÉTAT (`ModeBadge`, taille `md` : fontSize 11px) —
                 --fs-label est le seul token du design system qui vaut 11px. Le tracking et le poids
                 sont délibérément alignés sur ceux du ModeBadge (`var(--tracking-label)` = .14em,
                 fontWeight 500) pour produire la même présence typographique perçue que « MODE —
                 NORMAL » (voir revue « harmonisation navigation / MODE »). */
              fontSize: 'var(--fs-label)',
              fontWeight: 500,
              letterSpacing: 'var(--tracking-label)',
              color: on ? 'var(--accent)' : 'var(--text-muted)',
              borderBottom: on ? '1px solid var(--accent)' : '1px solid transparent',
              transition: 'var(--t-hover)',
            }}
          >
            {item.icon && <Icon name={item.icon} size={16} />}
            {item.label}
          </button>
        );
      })}
      {trailing && (
        <>
          <span
            aria-hidden="true"
            style={{ width: 1, alignSelf: 'stretch', minHeight: 20, background: 'var(--accent-line)' }}
          />
          {trailing}
        </>
      )}
    </nav>
  );
}
